import { Inject, Injectable } from '@angular/core';
import { Observable, BehaviorSubject, interval, Subject, switchMap, takeUntil, catchError, of, tap, startWith, first, map, finalize } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { BaseApiClientAssessmentService } from 'src/app/api/base-api-client-assessment.service';
import { API_CLIENT_ASSESSMENTS_SERVICE } from 'src/app/api/injectors/api-client-ai-assessments.token';
import { AssessmentLockStatus, AssessmentResourceId } from 'src/app/api/models/assessments/assessment.interface';
import { AssessmentIdleModeDialogComponent } from 'src/app/collaboration/assessments/assessment-page/assessment-idle-mode-dialog/assessment-idle-mode-dialog.component';
import { DialogsManagerService } from 'src/app/services/dialogs-manager.service';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AiAssessmentsLockStatusService {
  private pollingInterval = 10_000;
  private stopPollingSubject = new Subject<void>();
  private lockStatusSubject = new BehaviorSubject<AssessmentLockStatus | null>(null);
  private pollingErrorsSubject = new Subject<HttpErrorResponse>();
  private pollingActive = false;
  private dialogRef = null;

  lockStatus$: Observable<AssessmentLockStatus | null> = this.lockStatusSubject.asObservable();
  pollingErrors$: Observable<HttpErrorResponse> = this.pollingErrorsSubject.asObservable();

  constructor(
    @Inject(API_CLIENT_ASSESSMENTS_SERVICE) private apiClientAiAssessmentsService: BaseApiClientAssessmentService,
    private dialogManager: DialogsManagerService
  ) {}

  startPollingLockStatus(instanceId: AssessmentResourceId): void {
    if (this.pollingActive) {
      return;
    }

    this.pollingActive = true;
    
    interval(this.pollingInterval).pipe(
      startWith(0),
      takeUntil(this.stopPollingSubject),
      switchMap(() =>
        this.apiClientAiAssessmentsService.getLockStatus(instanceId as string).pipe(
          catchError((error: HttpErrorResponse) => {
            if (error.status >= 400 && error.status < 500) {
              this.pollingErrorsSubject.next(error);
              this.stopPolling();
            } else {
              console.error(`Polling error for instance ${instanceId}:`, error);
            }
            return of(null);
          })
        )
      ),
      tap((lockStatus) => this.lockStatusSubject.next(lockStatus))
    ).subscribe();
  }

  handleIdleDialogLogic(lockStatus: AssessmentLockStatus, email: string): Observable<boolean> {
    if (!lockStatus || lockStatus.lockedBy !== email) {
      return of(null);
    }

    const lockedAtTime = new Date(lockStatus.lockedAt).getTime();
    const currentTime = Date.now();
    const timeElapsed = currentTime - lockedAtTime;

    if (this.dialogRef || timeElapsed < environment.collaboration.idleSessionDialog.openDialogCountdown) {
      return of(null);
    }
    
    this.dialogRef = this.dialogManager
      .openDialogCommon(AssessmentIdleModeDialogComponent, null, '560px', false)
      .afterClosed$.pipe(
        first(),
        map(continueEditing => !!continueEditing),
        finalize(() => {
          this.dialogRef = null;
        })
    );

    return this.dialogRef;
  }

  stopPolling(): void {
    if (!this.pollingActive) {
      return;
    }

    this.pollingActive = false;
    this.lockStatusSubject.next(null);
    this.stopPollingSubject.next();
    this.stopPollingSubject.complete();
    this.stopPollingSubject = new Subject<void>();
  }

  updateLockStatus(lockStatus: AssessmentLockStatus): void {
    this.lockStatusSubject.next(lockStatus);
  }

  getLockStatus(instanceId: AssessmentResourceId): Observable<AssessmentLockStatus> {
    return this.apiClientAiAssessmentsService.getLockStatus(instanceId as string);
  }

  isPollingActive(): boolean {
    return this.pollingActive;
  }
}